import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import {
  getStatusProgress,
  isTicketCompleted,
  getCategoryName,
  getTicketTitle,
  getDisplayDate,
  formatTicketDate,
} from '@/utils/residents/maintenance';
import { MaintenanceProgressBar } from '@/components/residents/maintenance/shared';

interface WorkRequestCardProps {
  ticket: IMaintenanceListResponse;
  onPress: () => void;
}

/**
 * WorkRequestCard - Card component for displaying maintenance tickets
 * Matches Figma design with category label, date, title, and 3-segment status bar
 */
export const WorkRequestCard: React.FC<WorkRequestCardProps> = ({
  ticket,
  onPress,
}) => {
  const progress = getStatusProgress(ticket);
  const completed = isTicketCompleted(ticket);
  const categoryLabel = getCategoryName(ticket);
  const displayDate = formatTicketDate(getDisplayDate(ticket), 'medium', '');
  const title = getTicketTitle(ticket);

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Header Row - Category Label + Date */}
      <View style={styles.headerRow}>
        <View style={styles.categoryLabel}>
          <Text style={styles.categoryLabelText}>{categoryLabel}</Text>
        </View>
        <Text style={styles.dateText}>{displayDate}</Text>
      </View>

      {/* Title */}
      <Text style={styles.title} numberOfLines={2}>
        {title}
      </Text>

      {/* Footer Row - Progress Bar + Icon */}
      <View style={styles.footerRow}>
        {/* 3-Segment Progress Bar */}
        <MaintenanceProgressBar progress={progress} variant="mobile" />

        {/* Status Icon */}
        {completed ? (
          <View style={styles.completedIcon}>
            <CheckIcon />
          </View>
        ) : (
          <View style={styles.wrenchIcon}>
            <RESIDENTS_SVG_COMPONENTS.WRENCH width={17} height={17} color={MARKETPLACE_COLORS.inputBorder} />
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

// Simple checkmark icon component
const CheckIcon: React.FC = () => (
  <View style={styles.checkIconContainer}>
    <View style={styles.checkmark}>
      <View style={styles.checkmarkShort} />
      <View style={styles.checkmarkLong} />
    </View>
  </View>
);

const styles = StyleSheet.create({
  card: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    padding: 20,
    gap: 14,
  },

  // Header Row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryLabel: {
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    borderRadius: 60,
    paddingHorizontal: 10,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryLabelText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 18,
  },
  dateText: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textMuted,
    lineHeight: 18,
  },

  // Title
  title: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },

  // Footer Row
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
  },

  // Icons
  wrenchIcon: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  completedIcon: {
    width: 21,
    height: 21,
    borderRadius: 10.5,
    backgroundColor: 'rgba(102, 206, 102, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmark: {
    width: 11.25,
    height: 8,
    position: 'relative',
  },
  checkmarkShort: {
    position: 'absolute',
    width: 2,
    height: 5,
    backgroundColor: '#66CE66',
    bottom: 0,
    left: 2,
    transform: [{ rotate: '-45deg' }],
    borderRadius: 1,
  },
  checkmarkLong: {
    position: 'absolute',
    width: 2,
    height: 8,
    backgroundColor: '#66CE66',
    bottom: 0,
    right: 2,
    transform: [{ rotate: '45deg' }],
    borderRadius: 1,
  },
});

export default WorkRequestCard;
